/**
 * CRM Influencer API client — wraps /api/crm/influencers/* endpoints.
 */

import { qs, fetchJson } from './http';

// ─── Types ───────────────────────────────────────────────

export interface DiscoveryResult {
  platform: string;
  platform_handle: string;
  platform_url: string | null;
  name: string;
  avatar_url: string | null;
  email: string | null;
  follower_count: number;
  engagement_rate: number;
  country: string;
  language: string;
  niche: string;
  bio: string;
}

export interface CrmInfluencer {
  id: number;
  platform: string;
  platform_handle: string;
  platform_url: string | null;
  name: string;
  avatar_url: string | null;
  email: string | null;
  phone: string | null;
  follower_count: number | null;
  engagement_rate: number | null;
  country: string | null;
  language: string | null;
  niche: string | null;
  bio: string | null;
  status: string;
  assigned_to: string | null;
  campaign_id: number | null;
  contact_id: number | null;
  enrichment_data: string;
  enriched_at: string | null;
  ai_summary: string | null;
  notes: string | null;
  tags: string;
  last_contacted_at: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CrmCampaign {
  id: number;
  name: string;
  description: string | null;
  product_name: string | null;
  status: string;
  budget: number | null;
  currency: string;
  start_date: string | null;
  end_date: string | null;
  target_count: number | null;
  notes: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  influencer_count?: number;
}

export interface InfluencerStats {
  total: number;
  by_status: { status: string; count: number }[];
  by_platform: { platform: string; count: number }[];
  contacted_this_month: number;
  replied_this_month: number;
}

// ─── Discovery ───────────────────────────────────────────

export function discoverInfluencers(opts?: {
  keyword?: string;
  platform?: string;
  country?: string;
  min_followers?: number;
  max_followers?: number;
}): Promise<{ items: DiscoveryResult[]; total: number; source: string }> {
  return fetchJson('/api/crm/influencers/discover' + qs(opts || {}));
}

// ─── Stats ───────────────────────────────────────────────

export function fetchInfluencerStats(): Promise<InfluencerStats> {
  return fetchJson('/api/crm/influencers/stats');
}

// ─── Influencers ─────────────────────────────────────────

export function fetchInfluencers(opts?: {
  search?: string;
  platform?: string;
  status?: string;
  campaign_id?: number;
  assigned_to?: string;
  country?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: CrmInfluencer[]; total: number }> {
  return fetchJson('/api/crm/influencers' + qs(opts || {}));
}

export function fetchInfluencerDetail(id: number): Promise<{
  influencer: CrmInfluencer;
  activities: any[];
  campaign: CrmCampaign | null;
}> {
  return fetchJson(`/api/crm/influencers/${id}`);
}

export function createInfluencer(data: Partial<CrmInfluencer>): Promise<{ influencer: CrmInfluencer }> {
  return fetchJson('/api/crm/influencers', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export function updateInfluencer(id: number, data: Partial<CrmInfluencer>): Promise<{ influencer: CrmInfluencer }> {
  return fetchJson(`/api/crm/influencers/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export function deleteInfluencer(id: number): Promise<{ success: boolean }> {
  return fetchJson(`/api/crm/influencers/${id}`, { method: 'DELETE' });
}

// ─── Campaigns ───────────────────────────────────────────

export function fetchCampaigns(opts?: {
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: CrmCampaign[]; total: number }> {
  return fetchJson('/api/crm/influencers/campaigns/list' + qs(opts || {}));
}

export function fetchCampaignDetail(id: number): Promise<{
  campaign: CrmCampaign;
  influencers: CrmInfluencer[];
  total_influencers: number;
}> {
  return fetchJson(`/api/crm/influencers/campaigns/${id}`);
}

export function createCampaign(data: Partial<CrmCampaign>): Promise<{ campaign: CrmCampaign }> {
  return fetchJson('/api/crm/influencers/campaigns', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export function updateCampaign(id: number, data: Partial<CrmCampaign>): Promise<{ campaign: CrmCampaign }> {
  return fetchJson(`/api/crm/influencers/campaigns/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export function deleteCampaign(id: number): Promise<{ success: boolean }> {
  return fetchJson(`/api/crm/influencers/campaigns/${id}`, { method: 'DELETE' });
}
